import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { debounce } from '@mui/material';
import { logEvent } from '~/app/application/shared/lib/tag';
import BaseButton from '~/app/core/presentation/components/base-button/base-button';
import LinkMailTo from '~/app/core/presentation/components/link-mail-to/link-mail-to';
import LinkWhatsapp from '~/app/core/presentation/components/link-whatsapp/link-whatsapp';
import useJwtInfo from '~/app/core/presentation/hooks/use-jwt-info';
import { APP_ENV } from '~/env';

import BaseSelectInput from '../../../../../../../core/presentation/components/base-select-input/base-select-input';
import AddressTextField from '../../../../../components/address-text-field/address-text-field';
import CNPJTextField from '../../../../../components/cnpj-text-field/cnpj-text-field';
import CompanyNameTextField from '../../../../../components/company-name-text-field/company-name-text-field';
import EmailTextField from '../../../../../components/email-text-field/email-text-field';
import PhoneTextField from '../../../../../components/phone-text-field/phone-text-field';
import { CompanyResponseModel } from '../../../../domain/models/companies/company-response-model';
import { CompanyModel } from '../../../../domain/models/company-model';
import { RegisterRoutes } from '../../../../routes/routes';
import {
  useCnpjValidateMutation,
  useCreateSubscriptionMutation,
  useGetOwnersMutation,
  useVerifyCnpjAvailabilityMutation,
  useSelfOnboardingMutation
} from '../../../../store/hooks';
import { useRegisterUserDataStore } from '../../../../store/slice/register-user-data-slice';
import { SignUpFormDataProps } from '../form-register';
import {
  CNPJAndSectorContainer,
  CompanyDataContainer,
  CompanyDetailsContainer,
  InfoAlert
} from './company-data-step-styles';
import { CompanyDataValidator } from './company-data.validator';
import { sectorValues } from './sector-values';

export type CompanyDataStepProps = {
  handleFormData: (values: SignUpFormDataProps) => void;
  formData: SignUpFormDataProps;
  isLoading?: boolean;
};

type CompanyDataForm = {
  cnpj: string;
  companyName: string;
  sector: string;
  address: string;
  phone: string;
  email: string;
};

const SUPPORT_EMAIL = 'suporte@frota162.com.br';

const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@');

  const getMaskedLocalPart = (local: string) => {
    const visibleChars = Math.min(local.length, 3);
    const maskedChars = '*'.repeat(local.length - visibleChars);
    return `${local.slice(0, visibleChars)}${maskedChars}`;
  };

  const getMaskedDomainPart = (domain: string) => {
    const domainParts = domain.split('.');
    if (domainParts.length <= 1) {
      return domain;
    }
    const maskedSubdomain = '*'.repeat(
      domainParts[domainParts.length - 2].length
    );
    const tld = domainParts[domainParts.length - 1];
    return `${maskedSubdomain}.${tld}`;
  };

  const maskedLocal = getMaskedLocalPart(localPart);
  const maskedDomain = getMaskedDomainPart(domain);

  return `${maskedLocal}@${maskedDomain}`;
};

const CompanyDataStep = ({
  handleFormData,
  formData
}: CompanyDataStepProps) => {
  const navigate = useNavigate();
  const planId = APP_ENV.FREE_PLAN_ID;

  const { t } = useTranslation(['register-company-step', 'register']);
  const [cnpjError, setCnpjError] = useState(false);
  const [existingCompanyEmail, setExistingCompanyEmail] = useState<string>('');

  const { control, handleSubmit, formState, setError } = useForm<CompanyModel>({
    resolver: CompanyDataValidator,
    mode: 'onChange',
    defaultValues: {
      cnpj: '',
      companyName: '',
      sector: '',
      address: '',
      phone: '',
      email: ''
    }
  });
  const { registerState, setRegisterState } = useRegisterUserDataStore();
  const [cnpjValidate] = useCnpjValidateMutation();
  const [getOwners] = useGetOwnersMutation();
  const [verifyCnpjAvailability, { isLoading }] =
    useVerifyCnpjAvailabilityMutation();
  const [selfOnboarding, { isLoading: isCreationLoading }] =
    useSelfOnboardingMutation();
  const jwtInfo = useJwtInfo();
  const [displayCompanyData, setDisplayCompanyData] = useState(false);
  const [createSubscription, { isLoading: isLoadingSubscription }] =
    useCreateSubscriptionMutation();
  const plans = registerState.plans;
  const cnpjValidateRequest = (values: CompanyModel) => {
    // Verifica disponibilidade no serviço de account
    verifyCnpjAvailability({ cnpj: values.cnpj })
      .unwrap()
      .then((response) => {
        if (response.exists) {
          // CNPJ já existe, buscar informações da empresa e mostrar erro
          cnpjValidate(values)
            .unwrap()
            .then(async (validateResponse) => {
              await handleOwners(validateResponse);
              setDisplayCompanyData(false);
              setCnpjError(true);
            })
            .catch(() => {
              setCnpjError(true);
              setDisplayCompanyData(false);
            });
        } else {
          // CNPJ disponível, mostrar campos para preenchimento manual
          setCnpjError(false);
          setDisplayCompanyData(true);
        }
      })
      .catch(() => {
        // Em caso de erro na verificação, mostrar erro
        setCnpjError(true);
        setDisplayCompanyData(false);
        setError('cnpj', {
          type: 'custom',
          message: 'error verifying cnpj availability'
        });
      });
  };

  const handleOwners = async (response: CompanyResponseModel) => {
    if (response.owners.length <= 0) setExistingCompanyEmail('');

    getOwners({ id: response._id })
      .unwrap()
      .then((owners) => {
        setExistingCompanyEmail(
          owners.length > 0 ? maskEmail(owners[0].email) : ''
        );
      })
      .catch(() => {
        setExistingCompanyEmail('');
      });
  };

  const createCompanyRequest = (values: {
    cnpj: string;
    name: string;
    street: string;
    phone: string;
    email: string;
  }) => {
    selfOnboarding({
      cnpj: values.cnpj,
      name: values.name,
      street: values.street,
      phone: values.phone,
      email: values.email,
      userId: (jwtInfo as any).user.id
    })
      .unwrap()
      .then(async (organization) => {
        // Salva a organization no registerState
        setRegisterState({
          ...formData,
          organization,
          companies: [
            {
              _id: organization.company.id,
              name: organization.company.name,
              subscriptions: []
            }
          ]
        });

        const freePlan = plans.find((plan) => plan.type === 'free');
        if (!freePlan) console.error('free plan not found');

        await createSubscription({
          organizationId: organization.id,
          planId: freePlan?._id ?? planId
        })
          .unwrap()
          .then((subscription) => {
            setRegisterState({
              ...formData,
              organization,
              companies: [
                {
                  _id: organization.company.id,
                  name: organization.company.name,
                  subscriptions: [subscription._id.toString()]
                }
              ]
            });
          })
          .catch(() => {
            console.error('error in create subscription');
          });

        handleFormData({
          ...formData,
          company: {
            _id: organization.company.id,
            name: organization.company.name
          }
        });

        navigate(RegisterRoutes.VEHICLES_DATA);

        logEvent({
          category: 'Dados da empresa/trigger',
          action: 'OnSubmit',
          label: 'step03_dados_empresa'
        });
      })
      .catch(() => {
        console.error('error in create company');
      });
  };

  const cnpjWatch = useWatch({ control, name: 'cnpj' });

  const handleCNPJLookup = useMemo(
    () =>
      debounce(() => {
        setDisplayCompanyData(true);
        cnpjValidateRequest({ cnpj: cnpjWatch.replace(/\D/g, '') });
      }, 500),
    [cnpjWatch]
  );

  useEffect(() => {
    setCnpjError(false);
    if (!formState.errors.cnpj && cnpjWatch?.length === 18) handleCNPJLookup();
  }, [cnpjWatch, formState.errors.cnpj, formState.dirtyFields.cnpj]);

  return (
    <CompanyDataContainer
      component='form'
      onSubmit={handleSubmit((data: CompanyDataForm) => {
        createCompanyRequest({
          cnpj: data.cnpj.replace(/\D/g, ''),
          name: data.companyName,
          street: data.address,
          phone: data.phone.replace(/\D/g, ''),
          email: data?.email !== '' ? data?.email : registerState.user.email
        });
      })}
    >
      {cnpjError && (
        <InfoAlert severity='info'>
          {existingCompanyEmail
            ? t('cnpjError.message', { maskedEmail: existingCompanyEmail })
            : t('cnpjError.messageNoEmail')}{' '}
          <LinkMailTo label={SUPPORT_EMAIL} mailto={SUPPORT_EMAIL} /> ou pelo
          WhatsApp:{' '}
          <LinkWhatsapp
            label='(11) 95480-1937'
            countryCode='55'
            phoneNumber='11954801937'
            message={t('cnpjError.whatsappMessage', { cnpjWatch })}
          />
        </InfoAlert>
      )}
      <CNPJAndSectorContainer>
        <CNPJTextField control={control} />
        <BaseSelectInput
          required
          control={control}
          name='sector'
          labelId='sector'
          label={t('register-company-step:sector')}
          values={sectorValues}
        />
      </CNPJAndSectorContainer>

      {displayCompanyData && (
        <CompanyDetailsContainer>
          <CompanyNameTextField control={control} isLoading={isLoading} />
          <AddressTextField control={control} isLoading={isLoading} />
          <PhoneTextField control={control} isLoading={isLoading} />
          <EmailTextField control={control} isLoading={isLoading} optional />
        </CompanyDetailsContainer>
      )}
      <BaseButton
        disableElevation
        type='submit'
        variant='contained'
        color='primary'
        isLoading={isLoading || isCreationLoading || isLoadingSubscription}
        disabled={!formState.isDirty || !formState.isValid}
      >
        {t('register:next')}
      </BaseButton>
    </CompanyDataContainer>
  );
};

export default CompanyDataStep;
